// src/pages/ProductDetailPage.tsx
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
// import { getProductById, getRecommendedProducts } from "@/data/products"; // Remove or comment out getRecommendedProducts
import { products, getProductById } from "@/data/products"; // Keep getProductById from mock data for now
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import ProductCard from "@/components/ProductCard";
import { Product } from "@/types";
import { ShoppingCart, Check } from "lucide-react";

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [recommendedProducts, setRecommendedProducts] = useState<Product[]>([]);
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const { addToCart } = useCart();
  
  // Mock user ID for tracking purposes - In a real app, this would come from authentication
  const mockUserId = "user_abc_123";

  useEffect(() => {
    if (id) {
      const foundProduct = getProductById(id);
      if (foundProduct) {
        setProduct(foundProduct);
        
        // Get user email if exists
        const userEmail = sessionStorage.getItem("jogngym_user_email");
        const userId = userEmail || mockUserId;
        
        // Track product view
        trackProductView(foundProduct.id, userId);
        
        // If user has provided email, increment view count and check if we've hit 5
        if (userEmail) {
          let viewCount = parseInt(sessionStorage.getItem("jogngym_product_view_count") || "0");
          
          // Store product IDs to avoid counting duplicate views
          const viewedProducts = JSON.parse(sessionStorage.getItem("jogngym_viewed_products") || "[]");
          
          // Only count if this product hasn't been viewed before
          if (!viewedProducts.includes(foundProduct.id)) {
            viewedProducts.push(foundProduct.id);
            viewCount += 1;
            
            sessionStorage.setItem("jogngym_viewed_products", JSON.stringify(viewedProducts));
            sessionStorage.setItem("jogngym_product_view_count", viewCount.toString());
            
            // If user has viewed exactly 5 products, trigger newsletter
            if (viewCount === 5) {
              triggerProductNewsletter(userEmail, viewedProducts);
            }
          }
        }
        
        // Fetch recommended products
        fetchRecommendations(foundProduct.id, userId);
      }
    }
  }, [id]);

  /**
   * Tracks a product view by sending data to the backend.
   * This triggers the Behavioral Analysis Agent.
   * @param productId The ID of the product viewed.
   * @param userId The ID of the viewing user.
   */
  const trackProductView = async (productId: string, userId: string) => {
    try {
      console.log(`Sending product view to backend: Product ID ${productId}, User ID ${userId}`);
      
      const response = await fetch('/api/track-view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, userId })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      console.log("Product view tracked successfully by backend.");
    } catch (error) {
      console.error("Error tracking product view:", error);
    }
  };

  /**
   * Tracks an 'add to cart' action by sending data to the backend.
   * This triggers the Behavioral Analysis Agent.
   * @param productId The ID of the product added to cart.
   * @param userId The ID of the user.
   * @param quantity The quantity added.
   */
  const trackAddToCart = async (productId: string, userId: string, quantity: number) => {
    try {
      console.log(`Sending add to cart to backend: Product ID ${productId}, User ID ${userId}, Quantity ${quantity}`);
      
      const response = await fetch('/api/track-add-to-cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, userId, quantity })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      console.log("Add to cart tracked successfully by backend.");
    } catch (error) {
      console.error("Error tracking add to cart:", error);
    }
  };

  /**
   * Fetches personalized product recommendations from the backend.
   * This interacts with the Product & Content Recommendation Agent.
   * @param currentProductId The ID of the product currently being viewed.
   * @param userId The ID of the user.
   */
  const fetchRecommendations = async (currentProductId: string, userId: string) => {
    try {
      console.log(`Workspaceing recommendations for user ${userId} and product ${currentProductId} from backend.`);
      
      const response = await fetch(`/api/recommendations?userId=${userId}&currentProductId=${currentProductId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: Product[] = await response.json();
      setRecommendedProducts(data);
      console.log("Recommendations fetched successfully:", data);
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      setRecommendedProducts([]); // Fallback to empty array on error
    }
  };

  // Add new function to trigger personalized newsletter
  const triggerProductNewsletter = async (email: string, productIds: string[]) => {
    try {
      const response = await fetch('/api/trigger-product-newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, productIds })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      console.log("Product newsletter request sent successfully");
    } catch (error) {
      console.error("Error requesting product newsletter:", error);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      // Add to cart locally (for UI)
      addToCart(product, 1);
      
      // Track add to cart (calls backend)
      trackAddToCart(product.id, mockUserId, 1);
      
      // Show success state
      setIsAddedToCart(true);
      setShowDialog(true);
      
      // Reset success state after 3 seconds
      setTimeout(() => {
        setIsAddedToCart(false);
      }, 3000);
    }
  };

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <p>Product not found.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="mb-8">
        <Link to="/products" className="text-emerald-600 hover:text-emerald-800">
          &larr; Back to Products
        </Link>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        {/* Product Image */}
        <div className="bg-white p-4 rounded-lg shadow-md">
          <img 
            src={product.image} 
            alt={product.name} 
            className="w-full h-auto object-contain rounded-md"
          />
        </div>
        
        {/* Product Details */}
        <div>
          <h1 className="text-3xl font-bold mb-2">{product.name}</h1>
          <p className="text-2xl text-emerald-700 font-bold mb-4">${product.price.toFixed(2)}</p>
          
          <div className="bg-slate-50 p-4 rounded-md mb-6">
            <h2 className="text-lg font-semibold mb-2">Product Description</h2>
            <p className="text-slate-700">{product.description}</p>
          </div>
          
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-2">Category</h2>
            <div className="inline-block bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">
              {product.category.charAt(0).toUpperCase() + product.category.slice(1)}
            </div>
          </div>
          
          <Button 
            variant="primary" 
            size="lg" 
            className="w-full md:w-auto"
            onClick={handleAddToCart}
            disabled={isAddedToCart}
          >
            {isAddedToCart ? (
              <>
                <Check className="mr-2 h-5 w-5" /> Added to Cart
              </>
            ) : (
              <>
                <ShoppingCart className="mr-2 h-5 w-5" /> Add to Cart
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Recommended Products Section */}
      {recommendedProducts.length > 0 && (
        <div className="mt-16">
          <h2 className="text-2xl font-bold mb-6">Recommended For You</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {recommendedProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      )}
      
      {/* Success Dialog */}
      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Item Added to Cart</AlertDialogTitle>
            <AlertDialogDescription>
              {product.name} has been added to your cart.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction asChild>
              <Link to="/cart">
                <Button variant="primary">View Cart</Button>
              </Link>
            </AlertDialogAction>
            <AlertDialogAction>Continue Shopping</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductDetailPage;